import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
  faRobot,
  faTachometerAlt,
  faFilter,
  faRedo,
  faTrash,
  faCheck,
  faBan,
  faEye,
  faTimes,
  faHistory,
  faEnvelope,
  faUser,
  faSyncAlt,
  faCrown,
  faCaretDown,
  faUserCircle,
  faSignOutAlt,
  faPlus,
  faSave,
  faEdit,
  faPowerOff,
  faMicrochip,
  faCalendarCheck,
  faTag,
  faAlignLeft,
  faCodeBranch,
  faCogs,
  faMobileAlt,
  faSms,
  faClock,
  faGlobe,
  faBriefcase,
  faMoon,
  faFire,
  faPaperPlane,
  faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import '../scss/AlertsNotifications.scss';
import BaseURL from '../assets/contants/BaseURL';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Alerts = () => {
  const navigate = useNavigate();

  // State for filters
  const [filters, setFilters] = useState({
    timeRange: 'today',
    craneSelection: 'all',
    alertType: 'all'
  });

  // State for UI
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [showAlertDetails, setShowAlertDetails] = useState(false);
  const [showRulePanel, setShowRulePanel] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);

  // State for notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    alertTypes: {
      critical: true,
      warning: true,
      info: false,
      customRule: true
    },
    deliveryMethods: {
      inApp: true,
      email: true,
      sms: false
    },
    schedule: {
      twentyFourSeven: true,
      businessHours: false,
      quietHours: false
    }
  });

  // State for rule form
  const [ruleForm, setRuleForm] = useState({
    name: '',
    severity: 'critical',
    description: '',
    conditions: [{ parameter: 'load', operator: 'greater-than', value: '', unit: 'tons' }],
    cranes: { all: true, crn001: false, crn002: false, crn003: false, crn004: false },
    notifications: { inApp: true, email: false, sms: false },
    status: 'active'
  });

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
      const fetchOrgLogo = async () => {
        try {
          const token = getAuthToken();
          const organizationId = sessionStorage.getItem('organization_id');
  
          const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json',
            }
          });
  
          if (response.ok) {
            const data = await response.json();
            if (data.data?.logo_url) { 
              setOrgLogo(data.data.logo_url);
            }
          }
        } catch (error) {
          console.error('Error fetching org logo:', error);
        }
      };
  
      fetchOrgLogo();
    }, []);

  // Alert data
  const [alerts, setAlerts] = useState([
    {
      id: 'ALT-00123',
      timestamp: '2023-06-15 08:23:45',
      craneId: 'CRN-001',
      craneName: 'Gantry Crane #1',
      type: 'Overload',
      severity: 'critical',
      message: 'Load exceeded maximum capacity by 15%',
      status: 'Active',
      triggeredValue: '15.8 tons (115% of capacity)',
      threshold: '13.7 tons',
      duration: '2 minutes 45 seconds',
      operator: 'John Smith (OP-0042)',
      fullMessage: 'Load exceeded maximum capacity by 15% during hoisting operation. Crane automatically stopped to prevent damage. Immediate action required.'
    },
    {
      id: 'ALT-00124',
      timestamp: '2023-06-15 08:45:12',
      craneId: 'CRN-002',
      craneName: 'Overhead Crane #2',
      type: 'Temperature',
      severity: 'warning',
      message: 'Motor temperature approaching threshold',
      status: 'Active',
      triggeredValue: '78°C',
      threshold: '85°C',
      duration: '15 minutes',
      operator: 'Sarah Johnson (OP-0045)',
      fullMessage: 'Motor temperature is approaching critical threshold. Monitor closely and schedule inspection if temperature continues to rise.'
    },
    {
      id: 'ALT-00125',
      timestamp: '2023-06-15 09:12:33',
      craneId: 'CRN-003',
      craneName: 'Jib Crane #3',
      type: 'Voltage',
      severity: 'critical',
      message: 'Voltage drop detected in hoist motor',
      status: 'Active',
      triggeredValue: '350V',
      threshold: '400V',
      duration: '3 seconds',
      operator: 'Michael Chen (OP-0048)',
      fullMessage: 'Significant voltage drop detected. Immediate electrical inspection required.'
    },
    {
      id: 'ALT-00126',
      timestamp: '2023-06-15 10:05:27',
      craneId: 'CRN-001',
      craneName: 'Gantry Crane #1',
      type: 'Maintenance',
      severity: 'info',
      message: 'Scheduled maintenance due in 3 days',
      status: 'Active',
      triggeredValue: 'Due Date: 2023-06-18',
      threshold: '3 days',
      duration: 'N/A',
      operator: 'System',
      fullMessage: 'Regular scheduled maintenance is due in 3 days. Please schedule accordingly.'
    },
    {
      id: 'ALT-00118',
      timestamp: '2023-06-14 16:30:15',
      craneId: 'CRN-004',
      craneName: 'Bridge Crane #4',
      type: 'Operation',
      severity: 'normal',
      message: 'Operation completed successfully',
      status: 'Resolved',
      triggeredValue: 'N/A',
      threshold: 'N/A',
      duration: 'N/A',
      operator: 'Emma Davis (OP-0050)',
      fullMessage: 'Crane operation completed successfully with no issues.'
    },
    {
      id: 'ALT-00117',
      timestamp: '2023-06-14 14:22:10',
      craneId: 'CRN-002',
      craneName: 'Overhead Crane #2',
      type: 'Custom Rule',
      severity: 'custom',
      message: 'Extended operation without break detected',
      status: 'Active',
      triggeredValue: '2.5 hours',
      threshold: '2 hours',
      duration: '30 minutes over',
      operator: 'System',
      fullMessage: 'Operator has been operating crane continuously for 2.5 hours without a break. Recommended break schedule: 15 min break every 2 hours.'
    }
  ]);

  // Stats data
  const stats = {
    critical: 12,
    warning: 24,
    info: 8,
    resolved: 42,
    customRules: 5
  };

  // Rules data
  const [rules, setRules] = useState([
    {
      id: 1,
      name: 'Extended Operation Alert',
      severity: 'critical',
      description: 'Triggers when a crane operates continuously for more than 2 hours without a break',
      cranes: 'All Cranes',
      activeSince: '2023-05-10',
      status: 'active'
    },
    {
      id: 2,
      name: 'Weekend Operation Alert',
      severity: 'warning',
      description: 'Notifies when any crane operates on weekends',
      cranes: 'All Cranes',
      activeSince: '2023-04-15',
      status: 'active'
    },
    {
      id: 3,
      name: 'High Ambient Temperature Alert',
      severity: 'info',
      description: 'Triggers when ambient temperature exceeds 35°C and crane is operating',
      cranes: 'CRN-001, CRN-002',
      activeSince: '2023-06-01',
      status: 'inactive'
    },
    {
      id: 4,
      name: 'Maintenance Due Alert',
      severity: 'custom',
      description: 'Triggers when maintenance is due within 7 days based on operation hours',
      cranes: 'CRN-003, CRN-004',
      activeSince: '2023-05-22',
      status: 'active'
    }
  ]);

  // Chart ref
  const trendsChartRef = useRef();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Chart data
  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Critical Alerts',
        data: [5, 8, 12, 6, 9, 3, 7],
        borderColor: '#e74c3c',
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true
      },
      {
        label: 'Warning Alerts',
        data: [10, 15, 8, 12, 6, 10, 14],
        borderColor: '#f39c12',
        backgroundColor: 'rgba(243, 156, 18, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true
      },
      {
        label: 'Info Alerts',
        data: [3, 5, 2, 4, 8, 3, 5],
        borderColor: '#1abc9c',
        backgroundColor: 'rgba(26, 188, 156, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Number of Alerts' }
      },
      x: {
        title: { display: true, text: 'Day of Week' }
      }
    }
  };

  // Timeline data
  const timelineEvents = [
    { time: 'Today, 08:23 AM', message: 'Critical Alert: Overload detected on CRN-001 (15.8 tons)', severity: 'critical' },
    { time: 'Today, 08:45 AM', message: 'Warning Alert: High temperature on CRN-002 hoist motor (78°C)', severity: 'warning' },
    { time: 'Today, 09:12 AM', message: 'Critical Alert: Voltage drop on CRN-003 hoist motor (350V)', severity: 'critical' }
  ];

  const getSeverityClass = (severity) => {
    switch(severity) {
      case 'critical': return 'severity-critical';
      case 'warning': return 'severity-warning';
      case 'info': return 'severity-info';
      case 'normal': return 'severity-normal';
      case 'custom': return 'severity-rule-engine';
      default: return '';
    }
  };

  const getSeverityBtnClass = (severity) => {
    switch(severity) {
      case 'critical': return 'critical';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'normal': return 'normal';
      case 'rule-engine': return 'rule-engine';
      default: return 'all';
    }
  };

  const handleSeverityFilter = (severity) => {
    setSelectedSeverity(severity);
    showNotification(`Filtering by ${severity} alerts`, 'success');
  };

  const handleApplyFilters = () => {
    showNotification('Filters applied successfully', 'success');
  };

  const handleResetFilters = () => {
    setFilters({
      timeRange: 'today',
      craneSelection: 'all',
      alertType: 'all'
    });
    setSelectedSeverity('all');
    showNotification('Filters reset', 'success');
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all resolved alerts?')) {
      setAlerts(alerts.filter(alert => alert.status !== 'Resolved'));
      showNotification('Resolved alerts cleared', 'success');
    }
  };

  const handleAlertAction = (alertId, action) => {
    if (action === 'resolve') {
      setAlerts(alerts.map(alert => 
        alert.id === alertId ? { ...alert, status: 'Resolved' } : alert
      ));
      showNotification('Alert resolved', 'success');
    } else if (action === 'delete') {
      setAlerts(alerts.filter(alert => alert.id !== alertId));
      showNotification('Alert deleted', 'success');
    } else if (action === 'details') {
      const alert = alerts.find(a => a.id === alertId);
      setSelectedAlert(alert);
      setShowAlertDetails(true);
    }
  };

  const handleSaveRule = (e) => {
    e.preventDefault();
    const newRule = {
      id: rules.length + 1,
      name: ruleForm.name,
      severity: ruleForm.severity,
      description: ruleForm.description,
      cranes: ruleForm.cranes.all ? 'All Cranes' : 
        Object.keys(ruleForm.cranes).filter(key => ruleForm.cranes[key]).map(key => key.toUpperCase()).join(', '),
      activeSince: new Date().toISOString().split('T')[0],
      status: ruleForm.status
    };
    setRules([...rules, newRule]);
    showNotification('Rule saved successfully!', 'success');
    setShowRulePanel(false);
    setRuleForm({
      name: '',
      severity: 'critical',
      description: '',
      conditions: [{ parameter: 'load', operator: 'greater-than', value: '', unit: 'tons' }],
      cranes: { all: true, crn001: false, crn002: false, crn003: false, crn004: false },
      notifications: { inApp: true, email: false, sms: false },
      status: 'active'
    });
  };

  const addCondition = () => {
    setRuleForm({
      ...ruleForm,
      conditions: [...ruleForm.conditions, { parameter: 'load', operator: 'greater-than', value: '', unit: 'tons' }]
    });
  };

  const removeCondition = (index) => {
    const newConditions = ruleForm.conditions.filter((_, i) => i !== index);
    setRuleForm({ ...ruleForm, conditions: newConditions });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...ruleForm.conditions];
    newConditions[index][field] = value;
    setRuleForm({ ...ruleForm, conditions: newConditions });
  };

  const toggleRuleStatus = (ruleId) => {
    setRules(rules.map(rule => 
      rule.id === ruleId ? { ...rule, status: rule.status === 'active' ? 'inactive' : 'active' } : rule
    ));
    showNotification('Rule status updated', 'success');
  };

  const handleSaveSettings = () => {
    showNotification('Settings saved successfully!', 'success');
  };

  const handleResetSettings = () => {
    setNotificationSettings({
      alertTypes: { critical: true, warning: true, info: false, customRule: true },
      deliveryMethods: { inApp: true, email: true, sms: false },
      schedule: { twentyFourSeven: true, businessHours: false, quietHours: false }
    });
    showNotification('Settings reset to defaults', 'success');
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  const getFilteredAlerts = () => {
    let filtered = [...alerts];
    
    if (selectedSeverity !== 'all') {
      filtered = filtered.filter(alert => alert.severity === selectedSeverity);
    }
    
    if (filters.craneSelection !== 'all') {
      filtered = filtered.filter(alert => alert.craneId === filters.craneSelection);
    }
    
    if (filters.alertType !== 'all') {
      filtered = filtered.filter(alert => alert.type.toLowerCase() === filters.alertType.toLowerCase());
    }
    
    return filtered;
  };

  const filteredAlerts = getFilteredAlerts();

  return (
    <div className="alerts-notifications-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
                      <div className="account-dropdown">
                        <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          {orgLogo ? (
                                            <img
                                              src={orgLogo}
                                              alt="Organization Logo"
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                border: '2px solid #4361ee'
                                              }}
                                              onError={() => setOrgLogo(null)}
                                            />
                                          ) : (
                                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                          )}
                          <span>{sessionStorage.getItem('username') || 'User'}</span>
                          <FontAwesomeIcon icon={faCaretDown} />
                        </div>
                        {isDropdownOpen && (
                          <div className="account-menu show">
                            <div className="account-menu-item plan-info">
                              <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                              <span>Basic Plan</span>
                            </div>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faUser} /> Profile
                            </button>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                            </button>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
        </div>

        {/* Page Title */}
        <div className="page-title">
          <h1>Alerts & Notifications</h1>
          <p>Monitor and manage system alerts and notifications</p>
        </div>

        {/* Alerts Dashboard Section */}
        <div className="section-header">
          <h2><FontAwesomeIcon icon={faTachometerAlt} /> Alerts Dashboard</h2>
        </div>

        {/* Alert Severity Filter */}
        <div className="severity-filter">
          {['all', 'critical', 'warning', 'info', 'normal', 'rule-engine'].map((severity) => (
            <button
              key={severity}
              className={`severity-btn ${getSeverityBtnClass(severity)} ${selectedSeverity === severity ? 'active' : ''}`}
              onClick={() => handleSeverityFilter(severity)}
            >
              <FontAwesomeIcon icon={
                severity === 'critical' ? faExclamationCircle :
                severity === 'warning' ? faExclamationTriangle :
                severity === 'info' ? faInfoCircle :
                severity === 'normal' ? faCheckCircle :
                severity === 'rule-engine' ? faRobot :
                faBell
              } />
              {severity === 'rule-engine' ? 'Custom Rules' : severity.charAt(0).toUpperCase() + severity.slice(1) + (severity !== 'all' ? '' : ' Alerts')}
            </button>
          ))}
        </div>

        {/* Alert Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card critical">
            <div className="stat-valuea critical">{stats.critical}</div>
            <div className="stat-label">Critical Alerts</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-valuea warning">{stats.warning}</div>
            <div className="stat-label">Warning Alerts</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat-card info">
            <div className="stat-valuea info">{stats.info}</div>
            <div className="stat-label">Info Alerts</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat-card normal">
            <div className="stat-valuea normal">{stats.resolved}</div>
            <div className="stat-label">Resolved Alerts</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat-card rule-engine">
            <div className="stat-valuea rule-engine">{stats.customRules}</div>
            <div className="stat-label">Custom Rules</div>
            <div className="stat-label">Active</div>
          </div>
        </div>

        {/* Charts and Timeline */}
        <div className="charts-timeline-row">
          <div className="trends-chart">
            <h3>Alert Trends (Last 7 Days)</h3>
            <div className="chart-wrapper">
              <Line ref={trendsChartRef} data={chartData} options={chartOptions} />
            </div>
          </div>
          <div className="timeline-container">
            <div className="timeline-header">
              <h3>Recent Alert Timeline</h3>
            </div>
            <div className="timeline">
              {timelineEvents.map((event, index) => (
                <div key={index} className={`timeline-item ${event.severity}`}>
                  <div className="timeline-time">{event.time}</div>
                  <div className="timeline-content">
                    <strong>{event.message}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Alert Controls */}
        <div className="alert-controls">
          <div className="filter-group">
            <label className="filter-label">Time Range</label>
            <select 
              className="filter-select"
              value={filters.timeRange}
              onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Crane Selection</label>
            <select 
              className="filter-select"
              value={filters.craneSelection}
              onChange={(e) => setFilters(prev => ({ ...prev, craneSelection: e.target.value }))}
            >
              <option value="all">All Cranes</option>
              <option value="CRN-001">Gantry Crane #1 (CRN-001)</option>
              <option value="CRN-002">Overhead Crane #2 (CRN-002)</option>
              <option value="CRN-003">Jib Crane #3 (CRN-003)</option>
              <option value="CRN-004">Bridge Crane #4 (CRN-004)</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Alert Type</label>
            <select 
              className="filter-select"
              value={filters.alertType}
              onChange={(e) => setFilters(prev => ({ ...prev, alertType: e.target.value }))}
            >
              <option value="all">All Types</option>
              <option value="overload">Overload</option>
              <option value="temperature">Temperature</option>
              <option value="voltage">Voltage</option>
              <option value="maintenance">Maintenance</option>
              <option value="operation">Operation</option>
              <option value="custom rule">Custom Rule</option>
            </select>
          </div>

          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              <FontAwesomeIcon icon={faFilter} /> Apply Filters
            </button>
            <button className="btn btn-secondary" onClick={handleResetFilters}>
              <FontAwesomeIcon icon={faRedo} /> Reset
            </button>
            <button className="btn btn-danger" onClick={handleClearAll}>
              <FontAwesomeIcon icon={faTrash} /> Clear All
            </button>
          </div>
        </div>

        {/* Alerts Table */}
        <div className="alerts-container">
          <table className="alerts-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Alert ID</th>
                <th>Crane ID</th>
                <th>Alert Type</th>
                <th>Severity</th>
                <th>Message</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map((alert) => (
                <tr key={alert.id}>
                  <td>{alert.timestamp}</td>
                  <td>{alert.id}</td>
                  <td>{alert.craneId}</td>
                  <td>{alert.type}</td>
                  <td>
                    <span className={`alert-severity ${getSeverityClass(alert.severity)}`}>
                      {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                    </span>
                  </td>
                  <td>{alert.message}</td>
                  <td>{alert.status}</td>
                  <td>
                    <div className="alert-actions">
                      {alert.status !== 'Resolved' && (
                        <>
                          <button className="action-btn resolve" onClick={() => handleAlertAction(alert.id, 'resolve')}>
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                          <button className="action-btn ignore">
                            <FontAwesomeIcon icon={faBan} />
                          </button>
                        </>
                      )}
                      <button className="action-btn delete" onClick={() => handleAlertAction(alert.id, 'delete')}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      <button className="action-btn" onClick={() => handleAlertAction(alert.id, 'details')}>
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                   </td> 
                 </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Alert Details Panel */}
        {showAlertDetails && selectedAlert && (
          <div className="alert-details">
            <div className="details-header">
              <h3>Alert Details</h3>
              <button className="btn btn-secondary" onClick={() => setShowAlertDetails(false)}>
                <FontAwesomeIcon icon={faTimes} /> Close
              </button>
            </div>
            <div className="details-content">
              <div>
                <div className="details-section">
                  <div className="details-label">Alert ID</div>
                  <div className="details-value">{selectedAlert.id}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Timestamp</div>
                  <div className="details-value">{selectedAlert.timestamp}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Crane ID</div>
                  <div className="details-value">{selectedAlert.craneId} ({selectedAlert.craneName})</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Alert Type</div>
                  <div className="details-value">{selectedAlert.type}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Severity</div>
                  <div className="details-value">
                    <span className={`alert-severity ${getSeverityClass(selectedAlert.severity)}`}>
                      {selectedAlert.severity.charAt(0).toUpperCase() + selectedAlert.severity.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <div className="details-section">
                  <div className="details-label">Status</div>
                  <div className="details-value">{selectedAlert.status}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Triggered Value</div>
                  <div className="details-value">{selectedAlert.triggeredValue}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Threshold</div>
                  <div className="details-value">{selectedAlert.threshold}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Duration</div>
                  <div className="details-value">{selectedAlert.duration}</div>
                </div>
                <div className="details-section">
                  <div className="details-label">Operator</div>
                  <div className="details-value">{selectedAlert.operator}</div>
                </div>
              </div>
            </div>
            <div className="details-section full-width">
              <div className="details-label">Full Message</div>
              <div className="details-value">{selectedAlert.fullMessage}</div>
            </div>
            <div className="action-buttons details-actions">
              <button className="btn btn-primary">
                <FontAwesomeIcon icon={faCheck} /> Resolve Alert
              </button>
              <button className="btn btn-secondary">
                <FontAwesomeIcon icon={faHistory} /> View Historical Data
              </button>
              <button className="btn btn-secondary">
                <FontAwesomeIcon icon={faEnvelope} /> Notify Supervisor
              </button>
            </div>
          </div>
        )}

        {/* Alert Rules Section */}
        <div className="section-header">
          <h2><FontAwesomeIcon icon={faRobot} /> Alert Rules</h2>
        </div>

        {/* Rule Engine Panel */}
        {showRulePanel && (
          <div className="rule-engine-panel">
            <div className="rule-header">
              <h3><FontAwesomeIcon icon={faPlusCircle} /> Create New Alert Rule</h3>
              <button className="btn btn-secondary" onClick={() => setShowRulePanel(false)}>
                <FontAwesomeIcon icon={faTimes} /> Close
              </button>
            </div>

            <form onSubmit={handleSaveRule}>
              <div className="rule-form">
                <div className="rule-form-group">
                  <label className="rule-label"><FontAwesomeIcon icon={faTag} /> Rule Name</label>
                  <input 
                    type="text" 
                    className="rule-input" 
                    placeholder="e.g., Overload Protection" 
                    required
                    value={ruleForm.name}
                    onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  />
                </div>

                <div className="rule-form-group">
                  <label className="rule-label"><FontAwesomeIcon icon={faExclamationTriangle} /> Severity Level</label>
                  <select 
                    className="rule-select"
                    value={ruleForm.severity}
                    onChange={(e) => setRuleForm({ ...ruleForm, severity: e.target.value })}
                  >
                    <option value="critical">Critical</option>
                    <option value="warning">Warning</option>
                    <option value="info">Info</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div className="rule-form-group full-width">
                  <label className="rule-label"><FontAwesomeIcon icon={faAlignLeft} /> Description</label>
                  <textarea 
                    className="rule-textarea" 
                    placeholder="Describe what this rule monitors and why it's important..."
                    value={ruleForm.description}
                    onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                  />
                </div>

                <div className="rule-form-group full-width">
                  <label className="rule-label"><FontAwesomeIcon icon={faCodeBranch} /> Conditions</label>
                  <div className="rule-conditions" id="rule-conditions">
                    {ruleForm.conditions.map((condition, index) => (
                      <div key={index} className="rule-condition">
                        <select 
                          className="rule-select"
                          value={condition.parameter}
                          onChange={(e) => updateCondition(index, 'parameter', e.target.value)}
                        >
                          <option value="load">Load</option>
                          <option value="temperature">Temperature</option>
                          <option value="voltage">Voltage</option>
                          <option value="duration">Duration</option>
                          <option value="operation-count">Operation Count</option>
                        </select>
                        <select 
                          className="rule-select"
                          value={condition.operator}
                          onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                        >
                          <option value="greater-than">Greater Than</option>
                          <option value="less-than">Less Than</option>
                          <option value="equals">Equals</option>
                          <option value="not-equals">Not Equals</option>
                        </select>
                        <input 
                          type="text" 
                          className="rule-input" 
                          placeholder="Value"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                        />
                        <select 
                          className="rule-select"
                          value={condition.unit}
                          onChange={(e) => updateCondition(index, 'unit', e.target.value)}
                        >
                          <option value="tons">Tons</option>
                          <option value="percent">%</option>
                          <option value="celsius">°C</option>
                          <option value="volts">V</option>
                          <option value="minutes">Minutes</option>
                        </select>
                        <button type="button" className="action-btn delete" onClick={() => removeCondition(index)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="add-condition" onClick={addCondition}>
                    <FontAwesomeIcon icon={faPlusCircle} /> Add Condition
                  </div>
                </div>

                <div className="rule-form-group">
                  <label className="rule-label"><FontAwesomeIcon icon={faCogs} /> Apply to Cranes</label>
                  <div className="crane-selection">
                    <div className="crane-checkbox">
                      <input 
                        type="checkbox" 
                        id="all-cranes" 
                        checked={ruleForm.cranes.all}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          cranes: { ...ruleForm.cranes, all: e.target.checked }
                        })}
                      />
                      <label htmlFor="all-cranes">All Cranes</label>
                    </div>
                    <div className="crane-checkbox">
                      <input 
                        type="checkbox" 
                        id="crn-001"
                        checked={ruleForm.cranes.crn001}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          cranes: { ...ruleForm.cranes, crn001: e.target.checked }
                        })}
                      />
                      <label htmlFor="crn-001">Gantry Crane #1 (CRN-001)</label>
                    </div>
                    <div className="crane-checkbox">
                      <input 
                        type="checkbox" 
                        id="crn-002"
                        checked={ruleForm.cranes.crn002}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          cranes: { ...ruleForm.cranes, crn002: e.target.checked }
                        })}
                      />
                      <label htmlFor="crn-002">Overhead Crane #2 (CRN-002)</label>
                    </div>
                    <div className="crane-checkbox">
                      <input 
                        type="checkbox" 
                        id="crn-003"
                        checked={ruleForm.cranes.crn003}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          cranes: { ...ruleForm.cranes, crn003: e.target.checked }
                        })}
                      />
                      <label htmlFor="crn-003">Jib Crane #3 (CRN-003)</label>
                    </div>
                    <div className="crane-checkbox">
                      <input 
                        type="checkbox" 
                        id="crn-004"
                        checked={ruleForm.cranes.crn004}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          cranes: { ...ruleForm.cranes, crn004: e.target.checked }
                        })}
                      />
                      <label htmlFor="crn-004">Bridge Crane #4 (CRN-004)</label>
                    </div>
                  </div>
                </div>

                <div className="rule-form-group">
                  <label className="rule-label"><FontAwesomeIcon icon={faBell} /> Notification Method</label>
                  <div className="notification-methods">
                    <div className="method-checkbox">
                      <input 
                        type="checkbox" 
                        id="in-app"
                        checked={ruleForm.notifications.inApp}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          notifications: { ...ruleForm.notifications, inApp: e.target.checked }
                        })}
                      />
                      <label htmlFor="in-app"><FontAwesomeIcon icon={faMobileAlt} /> In-App</label>
                    </div>
                    <div className="method-checkbox">
                      <input 
                        type="checkbox" 
                        id="email"
                        checked={ruleForm.notifications.email}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          notifications: { ...ruleForm.notifications, email: e.target.checked }
                        })}
                      />
                      <label htmlFor="email"><FontAwesomeIcon icon={faEnvelope} /> Email</label>
                    </div>
                    <div className="method-checkbox">
                      <input 
                        type="checkbox" 
                        id="sms"
                        checked={ruleForm.notifications.sms}
                        onChange={(e) => setRuleForm({ 
                          ...ruleForm, 
                          notifications: { ...ruleForm.notifications, sms: e.target.checked }
                        })}
                      />
                      <label htmlFor="sms"><FontAwesomeIcon icon={faSms} /> SMS</label>
                    </div>
                  </div>
                </div>

                <div className="rule-form-group">
                  <label className="rule-label"><FontAwesomeIcon icon={faPowerOff} /> Status</label>
                  <div className="status-toggle">
                    <input 
                      type="radio" 
                      name="status" 
                      id="active" 
                      value="active" 
                      checked={ruleForm.status === 'active'}
                      onChange={(e) => setRuleForm({ ...ruleForm, status: e.target.value })}
                    />
                    <label htmlFor="active">Active</label>
                    <input 
                      type="radio" 
                      name="status" 
                      id="inactive" 
                      value="inactive"
                      checked={ruleForm.status === 'inactive'}
                      onChange={(e) => setRuleForm({ ...ruleForm, status: e.target.value })}
                    />
                    <label htmlFor="inactive">Inactive</label>
                  </div>
                </div>

                <div className="rule-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRulePanel(false)}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <FontAwesomeIcon icon={faSave} /> Save Rule
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Existing Rules List */}
        <div className="rules-list">
          <div className="rule-header">
            <h3>Active Alert Rules</h3>
            <div className="rule-search">
              <input type="text" placeholder="Search rules..." className="search-input" />
              <button className="btn btn-purple" onClick={() => setShowRulePanel(true)}>
                <FontAwesomeIcon icon={faPlus} /> New Rule
              </button>
            </div>
          </div>

          <div className="rules-grid">
            {rules.map((rule) => (
              <div key={rule.id} className={`rule-card ${rule.severity}`}>
                <div className="rule-card-header">
                  <h4>{rule.name}</h4>
                  <div className="rule-severity">{rule.severity.charAt(0).toUpperCase() + rule.severity.slice(1)}</div>
                </div>
                <div className="rule-card-body">
                  <p>{rule.description}</p>
                  <div className="rule-meta">
                    <span><FontAwesomeIcon icon={faMicrochip} /> {rule.cranes}</span>
                    <span><FontAwesomeIcon icon={faCalendarCheck} /> Active since {rule.activeSince}</span>
                  </div>
                </div>
                <div className="rule-card-footer">
                  <button className="btn btn-sm btn-edit">
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                  <button 
                    className={`btn btn-sm btn-toggle ${rule.status === 'active' ? 'active' : ''}`}
                    onClick={() => toggleRuleStatus(rule.id)}
                  >
                    <FontAwesomeIcon icon={faPowerOff} /> {rule.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notification Settings Section */}
        <div className="section-header">
          <h2><FontAwesomeIcon icon={faBell} /> Notification Settings</h2>
        </div>

        <div className="settings-container">
          <div className="settings-intro">
            <p>Configure how you receive alerts and notifications. Customize your preferences for different alert types and notification methods.</p>
          </div>

          <div className="settings-grid">
            {/* Alert Types Card */}
            <div className="settings-card">
              <div className="settings-card-header">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <h3>Alert Type Preferences</h3>
              </div>
              <div className="settings-card-body">
                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faFire} style={{ color: '#e74c3c' }} />
                    <div>
                      <h4>Critical Alerts</h4>
                      <p>Immediate attention required</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.alertTypes.critical}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        alertTypes: { ...notificationSettings.alertTypes, critical: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f39c12' }} />
                    <div>
                      <h4>Warning Alerts</h4>
                      <p>Potential issues that need monitoring</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.alertTypes.warning}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        alertTypes: { ...notificationSettings.alertTypes, warning: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#1abc9c' }} />
                    <div>
                      <h4>Info Alerts</h4>
                      <p>Informational messages</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.alertTypes.info}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        alertTypes: { ...notificationSettings.alertTypes, info: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faRobot} style={{ color: '#9b59b6' }} />
                    <div>
                      <h4>Custom Rule Alerts</h4>
                      <p>Alerts from your custom rules</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.alertTypes.customRule}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        alertTypes: { ...notificationSettings.alertTypes, customRule: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Notification Methods Card */}
            <div className="settings-card">
              <div className="settings-card-header">
                <FontAwesomeIcon icon={faPaperPlane} />
                <h3>Delivery Methods</h3>
              </div>
              <div className="settings-card-body">
                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#3498db' }} />
                    <div>
                      <h4>In-App Notifications</h4>
                      <p>Receive alerts within the application</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.deliveryMethods.inApp}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        deliveryMethods: { ...notificationSettings.deliveryMethods, inApp: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faEnvelope} style={{ color: '#2980b9' }} />
                    <div>
                      <h4>Email Notifications</h4>
                      <p>Alerts sent to your registered email</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.deliveryMethods.email}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        deliveryMethods: { ...notificationSettings.deliveryMethods, email: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faSms} style={{ color: '#2ecc71' }} />
                    <div>
                      <h4>SMS Notifications</h4>
                      <p>Text messages for critical alerts</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.deliveryMethods.sms}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        deliveryMethods: { ...notificationSettings.deliveryMethods, sms: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Notification Schedule Card */}
            <div className="settings-card">
              <div className="settings-card-header">
                <FontAwesomeIcon icon={faClock} />
                <h3>Notification Schedule</h3>
              </div>
              <div className="settings-card-body">
                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faGlobe} style={{ color: '#2c3e50' }} />
                    <div>
                      <h4>Receive notifications 24/7</h4>
                      <p>Get alerts at any time</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.schedule.twentyFourSeven}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        schedule: { ...notificationSettings.schedule, twentyFourSeven: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faBriefcase} style={{ color: '#95a5a6' }} />
                    <div>
                      <h4>Business hours only</h4>
                      <p>8:00 AM - 6:00 PM, Monday-Friday</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.schedule.businessHours}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        schedule: { ...notificationSettings.schedule, businessHours: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-item">
                  <div className="settings-item-info">
                    <FontAwesomeIcon icon={faMoon} style={{ color: '#2c3e50' }} />
                    <div>
                      <h4>Quiet hours</h4>
                      <p>No notifications between 10:00 PM - 6:00 AM</p>
                    </div>
                  </div>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.schedule.quietHours}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        schedule: { ...notificationSettings.schedule, quietHours: e.target.checked }
                      })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-actions">
            <button className="btn btn-primary" onClick={handleSaveSettings}>
              <FontAwesomeIcon icon={faSave} /> Save Settings
            </button>
            <button className="btn btn-secondary" onClick={handleResetSettings}>
              <FontAwesomeIcon icon={faRedo} /> Reset to Defaults
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default Alerts;